/**
 * useBuildVersionCheck — detecte si un nouveau deploy est sorti pendant que
 * le user travaille. Pattern : polling de /build-id.json toutes les 60s,
 * compare au __BUILD__ injecte au build courant. Return le new build_id si
 * different (= deploy detecte), sinon null.
 *
 * Cable au root dans <App /> via <NewVersionBanner /> — un toast persistant
 * bas-droite invite le user a recharger manuellement. Jamais d'auto-reload
 * (le user garde son form / brouillon en etat intact jusqu'au clic).
 *
 * Cas edge :
 * - Dev (HMR) → skip complet (le build_id change a chaque rebuild)
 * - Tab hidden → skip check (inutile, optim batterie mobile)
 * - Network error → silent retry a l'interval suivant
 * - Focus retour sur la tab → refetch immediat (user revenu d'une longue
 *   pause potentielle, on veut la fraicheur rapide)
 * - Une fois detecte, on stop le polling (evite le bruit jusqu'au reload)
 */

import { useEffect, useState } from 'react'

declare const __BUILD__: string

const POLL_INTERVAL_MS = 60_000

export function useBuildVersionCheck(): string | null {
  const [newBuildId, setNewBuildId] = useState<string | null>(null)

  useEffect(() => {
    if (import.meta.env.DEV) return
    if (newBuildId) return

    let cancelled = false

    const check = async () => {
      if (document.hidden) return
      try {
        const r = await fetch('/build-id.json?t=' + Date.now(), { cache: 'no-store' })
        if (!r.ok) return
        const data = (await r.json()) as { build_id?: string }
        if (!cancelled && data.build_id && data.build_id !== __BUILD__) {
          setNewBuildId(data.build_id)
        }
      } catch {
        // Network/parse error : silent, retry next interval
      }
    }

    void check()
    const interval = setInterval(check, POLL_INTERVAL_MS)
    const onFocus = () => void check()
    window.addEventListener('focus', onFocus)

    return () => {
      cancelled = true
      clearInterval(interval)
      window.removeEventListener('focus', onFocus)
    }
  }, [newBuildId])

  return newBuildId
}
